/**
 * Chat Error Logging System
 *
 * Comprehensive error logging with context, user info, and debugging data
 * for chat operations. Integrates with existing systemMonitoring.ts patterns.
 */

import { ChatError, ErrorContext } from "./chatErrors";

export interface ChatErrorLog {
  id: string;
  timestamp: number;
  error: {
    name: string;
    message: string;
    code: string;
    severity: "low" | "medium" | "high" | "critical";
    stack?: string;
  };
  context: ErrorContext;
  user: {
    id?: string;
    organizationId?: string;
    sessionId?: string;
  };
  system: {
    userAgent?: string;
    url?: string;
    viewport?: { width: number; height: number };
    timestamp: number;
  };
  recovery: {
    attempted: boolean;
    successful?: boolean;
    retryCount: number;
    fallbackUsed?: string;
  };
}

export interface ErrorMetrics {
  totalErrors: number;
  errorsByType: Record<string, number>;
  errorsBySeverity: Record<string, number>;
  recentErrors: ChatErrorLog[];
  recoveryRate: number;
  averageRecoveryTime: number;
}

/**
 * Chat Error Logger Class
 * Handles structured logging of chat errors with context and metrics
 */
export class ChatErrorLogger {
  private logs: ChatErrorLog[] = [];
  private maxLogs: number = 1000;
  private storageKey: string = "manageclean_chat_error_logs";
  private metricsKey: string = "manageclean_chat_error_metrics";

  constructor(maxLogs: number = 1000) {
    this.maxLogs = maxLogs;
    this.loadFromStorage();
  }

  /**
   * Log a chat error with full context
   */
  logError(
    error: ChatError | Error,
    context: Partial<ErrorContext> = {},
    userInfo: { id?: string; organizationId?: string; sessionId?: string } = {}
  ): string {
    const errorId = this.generateErrorId();
    const timestamp = Date.now();

    // Extract error information
    const errorInfo =
      error instanceof ChatError
        ? {
            name: error.name,
            message: error.message,
            code: error.code,
            severity: error.severity,
            stack: error.stack,
          }
        : {
            name: error.name || "Error",
            message: error.message,
            code: "UNKNOWN_ERROR",
            severity: "medium" as const,
            stack: error.stack,
          };

    // Gather system context
    const systemContext = {
      userAgent: navigator?.userAgent,
      url: window?.location?.href,
      viewport: {
        width: window?.innerWidth || 0,
        height: window?.innerHeight || 0,
      },
      timestamp,
    };

    // Create error log entry
    const errorLog: ChatErrorLog = {
      id: errorId,
      timestamp,
      error: errorInfo,
      context: {
        timestamp,
        ...context,
      },
      user: userInfo,
      system: systemContext,
      recovery: {
        attempted: false,
        retryCount: 0,
      },
    };

    // Add to logs
    this.logs.unshift(errorLog);

    // Maintain log size limit
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Save to storage
    this.saveToStorage();

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Chat Error Logged:", errorLog);
    }

    // Send to external monitoring service if configured
    this.sendToMonitoringService(errorLog);

    return errorId;
  }

  /**
   * Log error recovery attempt
   */
  logRecoveryAttempt(
    errorId: string,
    successful: boolean,
    fallbackUsed?: string,
    recoveryTime?: number
  ): void {
    const errorLog = this.logs.find((log) => log.id === errorId);
    if (errorLog) {
      errorLog.recovery.attempted = true;
      errorLog.recovery.successful = successful;
      errorLog.recovery.retryCount += 1;
      if (fallbackUsed) {
        errorLog.recovery.fallbackUsed = fallbackUsed;
      }

      this.saveToStorage();

      if (process.env.NODE_ENV === "development") {
        console.log("Recovery attempt logged:", {
          errorId,
          successful,
          fallbackUsed,
          recoveryTime,
        });
      }
    }
  }

  /**
   * Get error metrics and statistics
   */
  getMetrics(): ErrorMetrics {
    const now = Date.now();
    const last24Hours = now - 24 * 60 * 60 * 1000;
    const recentLogs = this.logs.filter((log) => log.timestamp > last24Hours);

    const errorsByType: Record<string, number> = {};
    const errorsBySeverity: Record<string, number> = {};
    let totalRecoveryAttempts = 0;
    let successfulRecoveries = 0;

    recentLogs.forEach((log) => {
      // Count by type
      errorsByType[log.error.code] = (errorsByType[log.error.code] || 0) + 1;

      // Count by severity
      errorsBySeverity[log.error.severity] = (errorsBySeverity[log.error.severity] || 0) + 1;

      // Count recovery attempts
      if (log.recovery.attempted) {
        totalRecoveryAttempts++;
        if (log.recovery.successful) {
          successfulRecoveries++;
        }
      }
    });

    const recoveryRate =
      totalRecoveryAttempts > 0 ? (successfulRecoveries / totalRecoveryAttempts) * 100 : 0;

    return {
      totalErrors: recentLogs.length,
      errorsByType,
      errorsBySeverity,
      recentErrors: recentLogs.slice(0, 10), // Last 10 errors
      recoveryRate,
      averageRecoveryTime: 0, // TODO: Implement recovery time tracking
    };
  }

  /**
   * Get errors by severity level
   */
  getErrorsBySeverity(severity: "low" | "medium" | "high" | "critical"): ChatErrorLog[] {
    return this.logs.filter((log) => log.error.severity === severity);
  }

  /**
   * Get errors by type/code
   */
  getErrorsByType(code: string): ChatErrorLog[] {
    return this.logs.filter((log) => log.error.code === code);
  }

  /**
   * Get errors for a specific user
   */
  getErrorsForUser(userId: string): ChatErrorLog[] {
    return this.logs.filter((log) => log.user.id === userId);
  }

  /**
   * Clear old logs (older than specified days)
   */
  clearOldLogs(daysToKeep: number = 7): void {
    const cutoffTime = Date.now() - daysToKeep * 24 * 60 * 60 * 1000;
    this.logs = this.logs.filter((log) => log.timestamp > cutoffTime);
    this.saveToStorage();
  }

  /**
   * Export logs for analysis
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `chat_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Save logs to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.logs));
      localStorage.setItem(this.metricsKey, JSON.stringify(this.getMetrics()));
    } catch (error) {
      console.warn("Failed to save error logs to storage:", error);
    }
  }

  /**
   * Load logs from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (error) {
      console.warn("Failed to load error logs from storage:", error);
      this.logs = [];
    }
  }

  /**
   * Send error to external monitoring service
   */
  private sendToMonitoringService(errorLog: ChatErrorLog): void {
    // TODO: Integrate with external monitoring service (e.g., Sentry, LogRocket)
    // This would be configured based on the project's monitoring setup

    // For now, just log critical errors to console
    if (errorLog.error.severity === "critical") {
      console.error("CRITICAL CHAT ERROR:", errorLog);
    }
  }
}

// Singleton instance for global use
export const chatErrorLogger = new ChatErrorLogger();

/**
 * Convenience function for logging errors
 */
export const logChatError = (
  error: ChatError | Error,
  context?: Partial<ErrorContext>,
  userInfo?: { id?: string; organizationId?: string; sessionId?: string }
): string => {
  return chatErrorLogger.logError(error, context, userInfo);
};

/**
 * Convenience function for logging recovery attempts
 */
export const logChatRecovery = (
  errorId: string,
  successful: boolean,
  fallbackUsed?: string,
  recoveryTime?: number
): void => {
  chatErrorLogger.logRecoveryAttempt(errorId, successful, fallbackUsed, recoveryTime);
};

/**
 * Hook for React components to use error logging
 */
export const useChatErrorLogger = () => {
  return {
    logError: logChatError,
    logRecovery: logChatRecovery,
    getMetrics: () => chatErrorLogger.getMetrics(),
    getErrorsBySeverity: (severity: "low" | "medium" | "high" | "critical") =>
      chatErrorLogger.getErrorsBySeverity(severity),
    clearOldLogs: (days?: number) => chatErrorLogger.clearOldLogs(days),
  };
};
